/* eslint-disable react/jsx-newline */

import * as React from "react";
import { Button } from "react-bootstrap";

import type { EvaluationResult } from "../../../../../../../../../server/ad-copy-analytics/simulation/lib/evaluations";
import type { AdCopyActivityWithBrand } from "../../../../../../../../../server/models/ad-copy/activity";
import type { AdCopyTestOnlyConceptKey } from "../../../../../../../../../server/models/ad-copy/concept";
import { AdCopyConceptTypeMap } from "../../../../../../../../../server/models/ad-copy/concept";
import type { AdCopyJob } from "../../../../../../../../../server/models/ad-copy/job";
import type { AdCopyCorrectionValueSettingProps } from "../correction-value";
import { AdCopyCorrectionValueSetting } from "../correction-value";
import {
	CommonCurrentProductData,
	SimulationPredictionData,
	SimulationReferenceSourceData,
} from "../../../../../../../../../server/types/request/simulation";
import { Constant } from "../../../../../../../../../server/analytics/v3/output";
import { AdCopyCP2 } from "../../../../../../../../../server/models/ad-copy/cp2";
import { AdCopySimulationResult } from "../result";
import { AdCopyNormTable } from "../norm-table";

type Props<T extends AdCopyTestOnlyConceptKey> = {
	activity: AdCopyActivityWithBrand;
	constValue: Constant;
	currentProduct?: CommonCurrentProductData<AdCopyCP2>;
	data: SimulationPredictionData;
	evaluationResult: EvaluationResult<T>;
	goalRecognitionRate: number;
	goalSalesVolume: number;
	hasEditRole: boolean;
	job: AdCopyJob;
	maxRecognition: number;
	onChangeRatio: AdCopyCorrectionValueSettingProps["onChangeRatio"];
	onSimulation: () => void;
	onSimulationSave: () => void;
	targetConceptType: T;
	referenceSourceData: SimulationReferenceSourceData;
};

const AdCopySimulationOneComponent = <T extends AdCopyTestOnlyConceptKey>({
	activity,
	constValue,
	currentProduct,
	data,
	evaluationResult,
	goalRecognitionRate,
	goalSalesVolume,
	hasEditRole,
	job,
	maxRecognition,
	onChangeRatio,
	onSimulation,
	onSimulationSave,
	targetConceptType,
	referenceSourceData,
}: Props<T>) => {
	return (
		<>
			{hasEditRole && (
				<>
					<Button onClick={onSimulationSave} style={{ margin: "10px 0px" }} variant="outline-secondary">
						保存
					</Button>

					<Button onClick={onSimulation} style={{ margin: "10px" }} variant="outline-secondary">
						画像Download
					</Button>
				</>
			)}
			<div className="simulation-container">
				<div className="simulation-headline row">
					<div className="concept-title item">
						<div className="content-left">提示コンセプト：{AdCopyConceptTypeMap[targetConceptType]}</div>
					</div>
					<div className="jobno item">
						<div className="content-right">
							JOB#: {job.jobNum}（{activity.category.name}、{activity.brand.name}）
						</div>
					</div>
				</div>

				<div className="simulation-body row">
					<div className="main item">
						<div className="image-area">
							<img src={data.conceptUrl} />
						</div>
					</div>
					<div className="column">
						<div className="key-indicators row">
							<div className="title-area item">
								<div className="rotate-label">主要指標</div>
							</div>
							<div className="graph-area item">
								<div className="content-left">
									<AdCopyNormTable evaluationResult={evaluationResult} targetConceptType={targetConceptType} />
									<div className="explain">＊購入意向はTB,T2Bをウエイト処理したワンナンバースコアで表示/計算</div>
								</div>
							</div>
						</div>
						<div className="separator row">
							<hr className="dashed" />
						</div>
						<div className="estimated-cp row">
							<div className="title-area item">
								<div className="rotate-label">推定CP（発売後12ヶ月）</div>
							</div>
							<div className="graph-area item">
								<div className="content">
									{/* 補整変数 */}
									<AdCopyCorrectionValueSetting
										correctionValue={data.correctionValue ?? {}}
										hasEditRole={hasEditRole}
										initInboundRatio={referenceSourceData.umi.gRatio_IBBY}
										initLocalRatio={referenceSourceData.umi.gRatio_LocalOther}
										maxRecognition={maxRecognition}
										onChangeRatio={onChangeRatio}
										goalRecognitionRate={goalRecognitionRate}
										gRepeat={referenceSourceData.buyPerYear.gRepeat}
									/>

									{/* 固定変数、判定基準、推定値、グラフ */}
									<AdCopySimulationResult
										constValue={constValue}
										currentProduct={currentProduct}
										data={data}
										goalSalesVolume={goalSalesVolume}
										goalRecognitionRate={goalRecognitionRate}
										referenceSourceData={referenceSourceData}
										isExistingBrand={activity.isExistingBrand ?? false}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export const AdCopySimulationOne = React.memo(AdCopySimulationOneComponent) as typeof AdCopySimulationOneComponent;
